import { useMutation } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dropzone, AcceptType } from '@/components/dropzone';
import { createSkill } from '@/services/skills';
import { AnatomyTree } from './anatomy-tree';

interface UploadSkillModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const UploadSkillModal = ({
  open,
  onClose,
  onSuccess,
}: UploadSkillModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [skillFile, setSkillFile] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: createSkill,
    onSuccess,
    retry: false,
  });

  const handleFilesChange = useCallback(
    (files: File[]) => {
      const file = files[0] ?? null;
      setSkillFile(file);
      mutation.reset();
      if (file && !name) {
        const baseName = file.name
          .replace(/\.(zip|skill)$/i, '')
          .replace(/[_-]/g, ' ');
        setName(baseName);
      }
    },
    [name, mutation],
  );

  const handleSubmit = () => {
    if (!skillFile || !name.trim()) return;
    mutation.mutate({
      name: name.trim(),
      description: description.trim(),
      skillFile,
    });
  };

  return (
    <Modal
      isDialogOpen={open}
      setIsDialogOpen={(v) => !v && onClose()}
      title='Upload Skill'
      description='Add a new skill for your Code Interpreter agents.'
      contentClassName='sm:max-w-[780px]'
      footer={
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!skillFile || !name.trim() || mutation.isPending}
          >
            <Upload className='size-4 mr-2' />
            {mutation.isPending ? 'Uploading...' : 'Upload Skill'}
          </Button>
        </div>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-[1fr_280px] gap-0'>
        {/* Form column */}
        <div className='flex flex-col gap-4 pr-0 sm:pr-6'>
          <div className='space-y-1.5'>
            <Label htmlFor='skill-name'>
              Name<span className='text-destructive ml-0.5'>*</span>
            </Label>
            <Input
              id='skill-name'
              placeholder='e.g. Fraud Detection, Financial Analysis'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='skill-desc'>Description</Label>
            <Textarea
              id='skill-desc'
              placeholder='What does this skill do? When should the agent reach for it?'
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className='space-y-1.5'>
            <Label>
              Skill archive<span className='text-destructive ml-0.5'>*</span>
            </Label>
            <Dropzone
              mode='manual'
              multiple={false}
              acceptType={AcceptType.ZIP}
              onFilesChange={handleFilesChange}
            />
            <p className='text-xs text-muted-foreground'>
              The archive must contain a{' '}
              <code className='font-mono text-[11px] rounded-sm bg-muted px-1 py-px'>
                SKILL.md
              </code>{' '}
              file at its root.
            </p>
          </div>

          {mutation.isError && (
            <p className='text-sm text-destructive'>
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Failed to upload skill. Please try again.'}
            </p>
          )}
        </div>

        {/* Reference column */}
        <aside className='hidden sm:flex flex-col gap-3 border-l border-border pl-6 py-1'>
          <p className='text-sm font-semibold'>What&apos;s inside a skill</p>
          <p className='text-xs text-muted-foreground leading-snug'>
            The .zip you upload should look like this — one SKILL.md at the
            root, plus whatever scripts and examples the skill calls on.
          </p>
          <AnatomyTree />
        </aside>
      </div>
    </Modal>
  );
};
